/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Objekt-Lookup tab — lists each record structure with its resolved object,
 *         similarity score, assignment status and a re-use of the existing
 *         mvs-ei-scoped-lookup component for candidate selection.
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import {Router} from '@angular/router';
import {EiImportRecordDto} from '../../../../../../../../model/dto/entity/ei-import-record.dto';
import {EiImportRecordStructureDto} from '../../../../../../../../model/dto/entity/ei-import-record-structure.dto';
import {
    EiImportRecordStructureAssignmentStatus
} from '../../../../../../../../enum/ei-import-record-structure-assignment-status.enum';
import {EiLookupScopeTypeEnum} from '../../../../../../../../enum/ei-lookup-scope-type.enum';

@Component({
    selector: 'ei-tooling-object-lookup-tab',
    templateUrl: './ei-tooling-object-lookup-tab.component.html',
    styleUrls: ['./ei-tooling-object-lookup-tab.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingObjectLookupTabComponent {

    @Input() record: EiImportRecordDto | null = null;
    @Input() recordStructures: EiImportRecordStructureDto[] = [];

    @Output() onReLookup: EventEmitter<{ recordId: number }> =
        new EventEmitter<{ recordId: number }>();
    @Output() onJumpToSearch: EventEmitter<{ structureId: number }> =
        new EventEmitter<{ structureId: number }>();

    /** Accordion state: all panels open by default. PrimeNG v20 requires a
     *  homogeneous array (string[] OR number[]); structure.id is number, so
     *  the array is typed as number[]. */
    activePanelValues: number[] = [];

    constructor(
        private readonly router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {
    }

    /**
     * Returns a German label for the assignment status.
     */
    assignmentStatusLabel(status: EiImportRecordStructureAssignmentStatus | null | undefined): string {
        if (status == null) {
            return 'Unbekannt';
        }
        switch (status) {
            case EiImportRecordStructureAssignmentStatus.NOT_FOUND: return 'Nicht gefunden';
            case EiImportRecordStructureAssignmentStatus.AUTO_FOUND: return 'Automatisch gefunden';
            case EiImportRecordStructureAssignmentStatus.AUTO_CREATION: return 'Automatisch angelegt';
            case EiImportRecordStructureAssignmentStatus.MANUAL_ASSIGNMENT: return 'Manuell zugewiesen';
            case EiImportRecordStructureAssignmentStatus.MANUAL_CREATION: return 'Manuell angelegt';
            case EiImportRecordStructureAssignmentStatus.QL_FOUND: return 'QL-Treffer';
            case EiImportRecordStructureAssignmentStatus.SCOPED_FOUND: return 'Scoped-Treffer';
            default: return 'Unbekannt';
        }
    }

    /**
     * Returns a PrimeNG severity keyword for the status tag.
     */
    statusSeverity(status: EiImportRecordStructureAssignmentStatus | null | undefined):
        'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status == null) {
            return 'secondary';
        }
        switch (status) {
            case EiImportRecordStructureAssignmentStatus.NOT_FOUND: return 'danger';
            case EiImportRecordStructureAssignmentStatus.AUTO_FOUND: return 'success';
            case EiImportRecordStructureAssignmentStatus.QL_FOUND: return 'success';
            case EiImportRecordStructureAssignmentStatus.SCOPED_FOUND: return 'success';
            case EiImportRecordStructureAssignmentStatus.MANUAL_ASSIGNMENT: return 'info';
            case EiImportRecordStructureAssignmentStatus.MANUAL_CREATION: return 'info';
            case EiImportRecordStructureAssignmentStatus.AUTO_CREATION: return 'info';
            default: return 'secondary';
        }
    }

    /**
     * Returns a German label for the resolved lookup scope.
     */
    scopeLabel(scope: EiLookupScopeTypeEnum | null | undefined): string {
        if (scope == null) {
            return 'Unbekannt';
        }
        switch (scope) {
            case EiLookupScopeTypeEnum.DIRECT: return 'Direkt';
            case EiLookupScopeTypeEnum.INDIRECT: return 'Indirekt';
            case EiLookupScopeTypeEnum.GLOBAL: return 'Global';
            default: return 'Unbekannt';
        }
    }

    /**
     * Emits a jump-to-search event so the shell switches to the search tab.
     */
    jumpToSearch(structureId: number): void {
        this.onJumpToSearch.emit({structureId});
    }

    /**
     * Emits the re-lookup event at record level.
     */
    triggerReLookup(): void {
        if (!this.record?.id) {
            return;
        }
        this.onReLookup.emit({recordId: this.record.id});
    }

    /**
     * Handles candidate-picked event from the scoped-lookup child component.
     */
    handleScopedAssigned(structureId: number, _event: { objectId: number; scope: EiLookupScopeTypeEnum }): void {
        if (!this.record?.id) {
            return;
        }
        // Re-lookup at record level to refresh derived data.
        this.onReLookup.emit({recordId: this.record.id});
    }

    /**
     * Returns the similarity as a percentage (0–100).
     */
    similarityPercent(sim: number | null | undefined): number {
        if (sim == null || !Number.isFinite(sim)) {
            return 0;
        }
        return Math.round(sim * 100);
    }
}
